import * as XLSX from 'xlsx';

// ─── JOB HISTORY PARSER ──────────────────────────────────────────────────────
// SCM Profit export (same format as open jobs but includes closed jobs)
// Columns: OFFICE, CLIENT FULLNAME, BOOKING NUMBER, JOB#, JOB TYPE,
//          JOB CREATEDON, EST REV AMOUNT, ACT REV AMOUNT,
//          EST COST AMOUNT, ACT COST AMOUNT, P/L AMOUNT, BASE CURRENCY, JOB STATUS

function excelDateToJS(serial) {
  if (!serial || isNaN(serial)) return null;
  const utc_days = Math.floor(serial - 25569);
  return new Date(utc_days * 86400 * 1000);
}

function daysBetween(date1, date2) {
  if (!date1 || !date2) return null;
  return Math.floor((date2 - date1) / (1000 * 60 * 60 * 24));
}

export function parseJobsHistory(buffer) {
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

  let headerIdx = rows.findIndex(r =>
    r.some(c => String(c).toUpperCase().includes('JOB#') ||
                String(c).toUpperCase().includes('JOB #') ||
                String(c).toUpperCase().includes('BOOKING NUMBER'))
  );
  if (headerIdx === -1) headerIdx = 0;

  const headers = rows[headerIdx].map(h => String(h).trim().toUpperCase());
  const col = (name) => headers.findIndex(h => h.includes(name));

  const COL = {
    office:    col('OFFICE'),
    client:    col('CLIENT'),
    booking:   col('BOOKING'),
    job:       col('JOB#') !== -1 ? col('JOB#') : col('JOB #'),
    jobType:   col('JOB TYPE'),
    createdOn: col('CREATEDON') !== -1 ? col('CREATEDON') : col('CREATED'),
    estRev:    col('EST REV'),
    actRev:    col('ACT REV'),
    estCost:   col('EST COST'),
    actCost:   col('ACT COST'),
    pl:        col('P/L'),
    currency:  col('CURRENCY'),
    status:    col('STATUS'),
  };

  const today = new Date();
  const jobs = [];

  for (let i = headerIdx + 1; i < rows.length; i++) {
    const r = rows[i];
    if (!r[COL.job] && !r[COL.client]) continue;

    const createdSerial = parseFloat(r[COL.createdOn]);
    const createdDateObj = excelDateToJS(createdSerial);
    const createdDate = createdDateObj ? createdDateObj.getTime() : null;
    const daysOpen = createdDateObj ? daysBetween(createdDateObj, today) : null;

    const estRev = parseFloat(r[COL.estRev]) || 0;
    const actRev = parseFloat(r[COL.actRev]) || 0;
    const actCost = parseFloat(r[COL.actCost]) || 0;
    const pl = parseFloat(r[COL.pl]) || 0;
    const status = String(r[COL.status] || '').trim();

    jobs.push({
      office:     String(r[COL.office] || '').trim(),
      client:     String(r[COL.client] || '').trim().replace(/\s+/g, ' '),
      bookingNo:  String(r[COL.booking] || '').trim(),
      jobNo:      String(r[COL.job] || '').trim(),
      jobType:    String(r[COL.jobType] || '').trim(),
      createdDate,
      daysOpen,
      estRev,
      actRev,
      actCost,
      pl,
      gpPct:      actRev > 0 ? (pl / actRev) * 100 : null,
      currency:   String(r[COL.currency] || 'SAR').trim(),
      status,
      // Alert flags
      isStaleOpen:       status === 'Initiated' && daysOpen !== null && daysOpen > 30,
      isClosedZeroRev:   (status === 'Closed' || status === 'ForcedClosed') && actRev === 0,
      isClosedZeroCost:  (status === 'Closed' || status === 'ForcedClosed') && actCost === 0,
      isCompletedNotClosed: status === 'Completed' && daysOpen !== null && daysOpen > 30,
    });
  }

  return jobs;
}

// ─── WEEKLY VOLUME STATS (8 weeks) ───────────────────────────────────────────
export function computeWeeklyVolumeStats(historyJobs) {
  const today = new Date();
  const weeks = Array.from({ length: 8 }, (_, w) => {
    const end = new Date(today - w * 7 * 24 * 60 * 60 * 1000);
    const start = new Date(today - (w + 1) * 7 * 24 * 60 * 60 * 1000);
    const label = w === 0 ? 'This wk' : w === 1 ? 'Last wk' : `${w + 1}wk ago`;

    const opened = historyJobs.filter(j =>
      j.createdDate && j.createdDate >= start.getTime() && j.createdDate < end.getTime()
    );
    const closed = historyJobs.filter(j =>
      j.status === 'Closed' &&
      j.createdDate && j.createdDate >= start.getTime() && j.createdDate < end.getTime()
    );

    // By office
    const tlsOpened = opened.filter(j => j.office === 'TLS').length;
    const tjedOpened = opened.filter(j => j.office === 'T-JED').length;
    const tlsClosed = closed.filter(j => j.office === 'TLS').length;
    const tjedClosed = closed.filter(j => j.office === 'T-JED').length;

    return {
      label,
      start,
      end,
      opened: opened.length,
      closed: closed.length,
      tlsOpened,
      tjedOpened,
      tlsClosed,
      tjedClosed,
      openedValue: opened.reduce((s, j) => s + j.estRev, 0),
      closedValue: closed.reduce((s, j) => s + j.actRev, 0),
    };
  });

  return weeks.reverse(); // chronological order, oldest first
}

// ─── CLIENT VOLUME SPARKLINE (8 weeks per client) ─────────────────────────────
export function computeClientVolumeSparklines(historyJobs) {
  const today = new Date();
  const weekStarts = Array.from({ length: 8 }, (_, w) =>
    new Date(today - (w + 1) * 7 * 24 * 60 * 60 * 1000).getTime()
  ).reverse(); // oldest first

  const byClient = {};
  historyJobs.forEach(j => {
    if (!byClient[j.client]) byClient[j.client] = [];
    byClient[j.client].push(j);
  });

  const sparklines = {};
  Object.keys(byClient).forEach(client => {
    const jobs = byClient[client];
    const weekCounts = weekStarts.map((start, i) => {
      const end = i < 7 ? weekStarts[i + 1] : today.getTime();
      return jobs.filter(j => j.createdDate && j.createdDate >= start && j.createdDate < end).length;
    });

    // Trend: compare last 2 weeks vs 2 weeks before that
    const recent = weekCounts.slice(6).reduce((s, v) => s + v, 0);
    const prior = weekCounts.slice(4, 6).reduce((s, v) => s + v, 0);
    const trend = recent > prior ? 'up' : recent < prior ? 'down' : 'flat';
    const isFallingOff = prior > 0 && recent === 0; // had activity, now gone

    sparklines[client] = { weekCounts, trend, isFallingOff };
  });

  return sparklines;
}

// ─── HISTORY ALERTS ──────────────────────────────────────────────────────────
export function computeHistoryAlerts(historyJobs) {
  const alerts = [];

  // 1. Jobs open > 30 days
  const staleOpen = historyJobs.filter(j => j.isStaleOpen);
  if (staleOpen.length > 0) {
    alerts.push({
      type: 'stale_open_30',
      severity: 'amber',
      message: `${staleOpen.length} jobs open for 30+ days with no close`,
      action: 'Review with ops — chase to close or update status',
      jobs: staleOpen,
    });
  }

  // 2. Closed jobs with zero revenue
  const closedZeroRev = historyJobs.filter(j => j.isClosedZeroRev);
  if (closedZeroRev.length > 0) {
    alerts.push({
      type: 'closed_zero_revenue',
      severity: 'red',
      message: `${closedZeroRev.length} closed jobs with zero revenue`,
      action: 'Investigate — work done, nothing invoiced or revenue not recorded',
      jobs: closedZeroRev,
    });
  }

  // 3. Closed jobs with zero cost
  const closedZeroCost = historyJobs.filter(j => j.isClosedZeroCost);
  if (closedZeroCost.length > 0) {
    alerts.push({
      type: 'closed_zero_cost',
      severity: 'amber',
      message: `${closedZeroCost.length} closed jobs with zero cost recorded`,
      action: 'Data integrity issue — costs missing from closed jobs',
      jobs: closedZeroCost,
    });
  }

  // 4. Office comparison
  const offices = ['TLS', 'T-JED'];
  offices.forEach(office => {
    const officeJobs = historyJobs.filter(j => j.office === office);
    const closed = officeJobs.filter(j => j.status === 'Closed').length;
    const total = officeJobs.length;
    const closureRate = total > 0 ? closed / total : 0;
    if (total > 5 && closureRate < 0.1) {
      alerts.push({
        type: 'low_closure_rate',
        severity: 'red',
        message: `${office}: only ${Math.round(closureRate * 100)}% closure rate (${closed}/${total} jobs closed)`,
        action: 'Review with office manager — jobs being opened but not closed',
      });
    }
  });

  return alerts;
}

// ─── GHOST JOBS ───────────────────────────────────────────────────────────────
// Jobs opened but nothing has happened — no cost, no revenue, sitting 30+ days

export function computeGhostJobs(historyJobs) {
  return historyJobs.filter(j =>
    j.status === 'Initiated' &&
    j.actRev === 0 &&
    j.actCost === 0 &&
    j.daysOpen !== null &&
    j.daysOpen > 30
  );
}

// ─── ESTIMATE VS ACTUAL GAP ──────────────────────────────────────────────────
// Jobs where actual revenue < 80% of estimated revenue (completed/closed only)

export function computeRevenueGap(historyJobs, dismissals = []) {
  const eligible = historyJobs.filter(j =>
    (j.status === 'Closed' || j.status === 'Completed' || j.status === 'ForcedClosed') &&
    j.estRev > 0 &&
    j.actRev >= 0 &&
    j.actRev < j.estRev * 0.8 &&
    !dismissals.includes(j.jobType)
  );

  // Group by client
  const byClient = {};
  eligible.forEach(j => {
    if (!byClient[j.client]) byClient[j.client] = { client: j.client, jobs: [], totalEst: 0, totalAct: 0 };
    byClient[j.client].jobs.push(j);
    byClient[j.client].totalEst += j.estRev;
    byClient[j.client].totalAct += j.actRev;
  });

  const clientSummary = Object.values(byClient).map(c => ({
    ...c,
    gapAmount: c.totalEst - c.totalAct,
    gapPct: c.totalEst > 0 ? Math.round((1 - c.totalAct / c.totalEst) * 100) : 0,
    jobCount: c.jobs.length,
  })).sort((a, b) => b.gapAmount - a.gapAmount);

  // Group by job type
  const byJobType = {};
  eligible.forEach(j => {
    if (!byJobType[j.jobType]) byJobType[j.jobType] = { jobType: j.jobType, jobs: [], totalEst: 0, totalAct: 0 };
    byJobType[j.jobType].jobs.push(j);
    byJobType[j.jobType].totalEst += j.estRev;
    byJobType[j.jobType].totalAct += j.actRev;
  });

  const jobTypeSummary = Object.values(byJobType).map(t => ({
    ...t,
    gapAmount: t.totalEst - t.totalAct,
    gapPct: t.totalEst > 0 ? Math.round((1 - t.totalAct / t.totalEst) * 100) : 0,
    jobCount: t.jobs.length,
  })).sort((a, b) => b.gapAmount - a.gapAmount);

  return { jobs: eligible, clientSummary, jobTypeSummary };
}
